/**
 * The i18n context and its hooks.
 *
 * Kept apart from `I18nProvider.tsx` so that file exports nothing but a
 * component — otherwise every edit to a hook invalidates the component for
 * React Fast Refresh and the editor state is lost on each keystroke.
 */
import { createContext, useContext } from 'react';
import { createTranslator, DEFAULT_LOCALE } from './translator';
import type { Translator } from './translator';

/**
 * Defaults to English rather than `null`: a component rendered outside the
 * provider — a test harness, an error boundary above it — should still produce
 * readable text instead of throwing.
 */
export const I18nContext = createContext<Translator>(createTranslator(DEFAULT_LOCALE));

export function useTranslator(): Translator {
  return useContext(I18nContext);
}

/** Shorthand for the common case: `const t = useT()`. */
export function useT(): Translator['t'] {
  return useContext(I18nContext).t;
}

/** The active locale, for components that branch on it (date and number formats). */
export function useLocale(): Translator['locale'] {
  return useContext(I18nContext).locale;
}
